import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import * as Yup from "yup";

import {
  AppSettingsDto,
  UpdateAppSettingsRequest,
} from "../../interfaces/app-settings";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { AppSettingsTypeEnum, ErrorHandlingTypeEnum } from "../../enums";
import { useFormik } from "formik";
import { ActivityButton } from "../../components/Common";
import {
  useGetAppSettingsQuery,
  useUpdateAppSettingsMutation,
} from "../../store/api/appSettingsApi";
import { ParsedError } from "../../interfaces";
import { toast } from "react-toastify";

export const AppVersionPage = () => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "controlAppAppVersion",
  })}`;

  //#region internal state
  const [request] = useState<AppSettingsDto>();
  //#endregion

  //#region api
  const {
    data: appSettings,
    isLoading: isLoadingAppSettings,
    isError: isErrorAppSettings,
  } = useGetAppSettingsQuery(AppSettingsTypeEnum.APPLICATION_VERSION);

  const [
    updateAppSettings,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
    },
  ] = useUpdateAppSettingsMutation();
  //#endregion

  //#region form
  const form = useFormik<UpdateAppSettingsRequest>({
    enableReinitialize: true,
    initialValues: {
      value: {
        en: request?.value.en || "",
        ar: request?.value.ar || "",
      },
    },
    validationSchema: Yup.object({
      value: Yup.object({
        en: Yup.string()
          .max(
            10,
            t("maxLength", { maxLength: 10, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            10,
            t("maxLength", { maxLength: 10, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
    }),
    onSubmit: (values) => {
      updateAppSettings({
        type: AppSettingsTypeEnum.APPLICATION_VERSION,
        data: values,
      });
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (appSettings) {
      form.setValues({
        value: appSettings.value,
      });
    }
  }, [appSettings, isLoadingAppSettings]);

  useEffect(() => {
    if (!isLoadingUpdate && isSuccessUpdate) {
      toast.success(t("savedSuccessfully"), {
        autoClose: 3000,
      });
    }
  }, [isLoadingUpdate, isSuccessUpdate]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingUpdate]);
  //#endregion

  return (
    <Row>
      <Col>
        <Card>
          <CardHeader>
            <h6 className="card-title mb-0">{t("applicationVersion")}</h6>
          </CardHeader>
          <CardBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
                return false;
              }}
              action="#"
            >
              <Row>
                <Col>
                  <div className="mb-3">
                    <label htmlFor="value.en" className="form-label">
                      {t("valueEn")}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      disabled={isErrorAppSettings}
                      autoComplete="off"
                      id="value.en"
                      name="value.en"
                      value={form.values?.value.en}
                      type="text"
                      className="form-control"
                      placeholder={t("enterInEnglish")}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      invalid={
                        form.touched.value?.en && form.errors.value?.en
                          ? true
                          : false
                      }
                    />
                    {form.touched.value?.en && form.errors.value?.en ? (
                      <FormFeedback type="invalid">
                        {form.errors.value?.en as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col>
                  <div className="mb-3">
                    <Label htmlFor="value.ar" className="form-label">
                      {t("valueAr")}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      disabled={isErrorAppSettings}
                      autoComplete="off"
                      id="value.ar"
                      name="value.ar"
                      value={form.values.value?.ar}
                      type="text"
                      className="form-control"
                      placeholder={t("enterInArabic")}
                      style={{
                        direction: "rtl",
                      }}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      invalid={
                        form.touched.value?.ar && form.errors.value?.ar
                          ? true
                          : false
                      }
                    />
                    {form.touched.value?.ar && form.errors.value?.ar ? (
                      <FormFeedback type="invalid">
                        {form.errors.value?.ar as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <ActivityButton
                    title="save"
                    inProgressTitle="saving"
                    color="success"
                    className="btn btn-success"
                    type="submit"
                    inProgress={isLoadingAppSettings || isLoadingUpdate}
                    disabled={isLoadingAppSettings || isLoadingUpdate}
                  ></ActivityButton>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
